'use client';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { TealiumUTag } from 'shared/types/tealium/TealiumUTag';
import { getCookie } from 'typescript-cookie';
import ErrorIcon from '@/components/customercenter-ui/svgs/notifications/error';
import SuccessIcon from '@/components/customercenter-ui/svgs/notifications/success';
import { convertDateFormat } from '@/components/customercenter-ui/utils/formatUtils';
import { triggerTealiumEvent, getPageUtagData } from '@/components/customercenter-ui/utils/tealiumUtils';
import { useAccount } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { useShowActiveBrandSection } from '@/frontastic/hooks/useShowActiveBrandSection';
import { ErrorNotification, SuccessNotification } from '@/helpers/utils/notification';
import { formatDateMoment, formatMobileNumber } from '@/helpers/utils/siteUtils';
import { CTAccount, ForgotPassword } from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import { EDIT_PHONE_NUMBER, EDIT_EMAIL, DATE_FORMAT, COOKIE_KEYS } from '../../../../../../constants/constant';
import CustomerPrefrenceModal from '../../../modals/customerDetailAction/customerPrefrence';
import EditCustomerDetailsModal from '../../../modals/customerDetailAction/editCustomerdetails';
import ManagePhoneEmailModal from '../../../modals/customerResult/managePhoneEmail';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import Loader from '../../../ui/loader';
import { useToast } from '../../../ui/use-toast';
import {
  CUSTOMER_DETAILS_HEADER_KEYS,
  DATE_FORMAT_MONTH_DATE_YYYY,
  ISO_DATE_TIME_FORMAT,
  PAYLOAD_QUERY,
} from '../../constants';
import { CustomerDetailsHeaderItem, EmailRecord, PhoneRecord } from '../../types';
import { ExtractFormData } from '../../utils';
import SoldAccountBanner from '../soldAccountBanner';

const CustomerDetails = ({
  CustomerAccountAddress,
  CustomerAccountData,
  Texts,
  emailRecords,
  phoneRecords,
  creditAccountId,
  customer,
}: {
  CustomerAccountAddress: CustomerDetailsHeaderItem[];
  CustomerAccountData: CustomerDetailsHeaderItem[];
  Texts: any;
  emailRecords: EmailRecord[];
  phoneRecords: PhoneRecord[];
  creditAccountId: string;
  customer: any;
}) => {
  const { toast } = useToast();
  const projectSettings: ProjectSettings = useProjSettings();
  const { forgotPassword, isCTAccount } = useAccount();
  const params = useSearchParams();
  const showActiveBrandSection = useShowActiveBrandSection();
  const [siteId, setSiteId] = useState<string>();
  const allowedCreditSites = JSON.parse(projectSettings?.allowedCreditSites ?? 'ZBSite');
  const isAllowedSite = allowedCreditSites?.creditSites?.includes(siteId);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [currentEditType, setCurrentEditType] = useState(EDIT_EMAIL);
  const [accountData, setAccountData] = useState(CustomerAccountData ?? []);
  const [accountAddress, setAccountAddress] = useState(CustomerAccountAddress ?? []);
  const [isCtAccount, setIsCtAccount] = useState(false);
  const formData = ExtractFormData(accountData, accountAddress) ?? {};
  const [state, setState] = useState('');
  const [creditSoldAccountId, setCreditSoldAccountId] = useState<string>('');
  const [isSoldAccount, setIsSoldAccount] = useState(false);
  const [debtBuyerCode, setDebtBuyerCode] = useState<string>('');
  const [accountSoldOn, setAccountSoldOn] = useState<string>('');

  const updateAccountAddress = (newValues: any) => {
    const updatedArray = accountAddress.map((item) => {
      const updatedValue = newValues[item?.key ?? item?.field];
      if (updatedValue !== undefined) {
        return { ...item, value: updatedValue };
      }
      return item;
    });

    const updateAccountData = accountData.map((item) => {
      const updatedValue = newValues[item?.key ?? item?.field];
      if (updatedValue !== undefined) {
        return { ...item, value: updatedValue };
      }
      return item;
    });
    setAccountAddress(updatedArray);
    setAccountData(updateAccountData);
  };

  useEffect(() => {
    const cookieSiteId = getCookie(COOKIE_KEYS.selectedSite) ?? 'ZBSite';
    setSiteId(cookieSiteId);
  }, []);

  const handleForgotPassword = async () => {
    setLoading(true);
    const payload = {
      query: {
        divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
      },
      data: {
        email: emailRecords?.[0]?.EmailInfo?.emailAddress,
      },
    } as ForgotPassword;

    await forgotPassword(payload).then((response) => {
      if (response?.success) {
        SuccessNotification(toast, {
          description: Texts.forgotPasswordEmailSent,
          action: <SuccessIcon className="h-5 w-5" />,
        });
      } else {
        ErrorNotification(toast, {
          description: response?.errorMessage,
          action: <ErrorIcon className="h-5 w-5" />,
        });
      }
    });
    setLoading(false);
  };

  useEffect(() => {
    const email = emailRecords?.[0]?.EmailInfo?.emailAddress;
    if (email) {
      setLoading(true);
      const ctAccount = async () => {
        const payload = {
          query: {
            divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
            email: email,
          },
        } as CTAccount;
        await isCTAccount(payload).then((response) => {
          setIsCtAccount(response?.data?.accountExistInCT);
          setLoading(false);
        });
      };
      ctAccount();
    }
  }, []);

  useEffect(() => {
    if (projectSettings) {
      const stateLocations = projectSettings?.shippingLocations
        ? JSON.parse(projectSettings?.shippingLocations)?.US
        : [];
      const currentState = stateLocations?.filter((items: any) => items.code === accountAddress[3]?.value);
      if (accountAddress[3]?.value && currentState) {
        setState(`${currentState?.[0]?.code} - ${currentState?.[0]?.name}`);
      } else {
        setState('');
      }
    }
  }, [projectSettings, accountAddress]);

  useEffect(() => {
    const pageLinkUtagData: TealiumUTag = getPageUtagData('Customer Details');
    triggerTealiumEvent(pageLinkUtagData);
  }, []);

  useEffect(() => {
    if (customer) {
      if (customer?.DivisionCustomers?.[0]?.CreditAccount?.AccountInfo?.isSoldAccount) {
        setCreditSoldAccountId(customer?.DivisionCustomers?.[0]?.CreditAccount?.creditAccountId ?? '');
        setIsSoldAccount(true);
        const debtBuyerCode = customer?.DivisionCustomers?.[0]?.CreditAccount?.AccountInfo?.debtBuyerCode;
        setDebtBuyerCode(debtBuyerCode ?? '');
        const accountSoldOn = customer?.DivisionCustomers?.[0]?.CreditAccount?.AccountInfo?.accountSoldOn;
        if (accountSoldOn) {
          setAccountSoldOn(convertDateFormat(accountSoldOn, ISO_DATE_TIME_FORMAT, DATE_FORMAT_MONTH_DATE_YYYY));
        }
      }
    }
  }, [customer]);

  return (
    <>
      <SoldAccountBanner
        isSoldAccount={isSoldAccount}
        accountSoldOn={accountSoldOn}
        creditAccountNumber={creditSoldAccountId}
        debtBuyerCode={debtBuyerCode}
      />
      <Card className="w-full rounded-md border-foggyGray shadow-lg">
        {loading && <Loader />}
        <ManagePhoneEmailModal
          emailRecords={emailRecords}
          phoneRecords={phoneRecords}
          accountData={accountData}
          openModal={openModal}
          setOpenModal={setOpenModal}
          currentEditType={currentEditType}
          setCurrentEditType={setCurrentEditType}
          setAccountData={setAccountData}
        />
        <CardHeader className="p-5">
          <CardTitle className="flex flex-wrap">
            <p className="mr-auto text-lg font-bold">{Texts.customerDetails}</p>
            {showActiveBrandSection && (
              <section className="flex flex-wrap gap-2">
                <Button
                  className="h-[30px] rounded-md border-[1px] border-foggyGray px-3 py-1.5 text-xs font-medium"
                  onClick={() => setOpenModal(true)}
                >
                  {Texts.manageEmailPhoneButton}
                </Button>
                {isCtAccount ? (
                  <Button
                    className="h-[30px] border-[1px] border-foggyGray px-3 py-1.5 text-xs font-medium"
                    onClick={handleForgotPassword}
                  >
                    {Texts.forgotPasswordButton}
                  </Button>
                ) : (
                  <></>
                )}
                <CustomerPrefrenceModal />
                <button
                  onClick={() => setOpen(true)}
                  className="block h-[30px] rounded-md border-[1px] border-foggyGray px-3 py-1.5 text-xs font-medium"
                >
                  {Texts.editCustomerDetailsButton}
                </button>
                {open && (
                  <EditCustomerDetailsModal
                    updateAccountAddress={updateAccountAddress}
                    formData={formData}
                    open={open}
                    setOpen={setOpen}
                    creditAccountId={creditAccountId}
                    isAllowedSite={isAllowedSite}
                  />
                )}
              </section>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid w-4/5 grid-cols-2 gap-4 text-sm font-normal">
            <div>
              {accountData?.map((items: any) => {
                const isSensitiveField = items?.key === 'dob' || items?.key === 'ssn';
                // Hide sensitive fields if not allowed
                if (isSensitiveField && !isAllowedSite) return null;

                return (
                  <div className="flex gap-2" key={`key-${items?.field}`}>
                    <div className="mb-3 w-72 flex-1">{items?.field}</div>
                    <div className={`flex-1 font-semibold ${items?.mask ? 'gbmask' : ''}`}>
                      {!Array.isArray(items?.value) ? (
                        <div>
                          {items?.key !== 'dob' ? (
                            <div>
                              {items?.key === 'ssn' ? 'XXXX-XX-' : ''} {items.value}
                            </div>
                          ) : (
                            <div>{formatDateMoment(items?.value, DATE_FORMAT)}</div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <p className="break-all">
                            {items?.key === 'phoneNumber' &&
                              formatMobileNumber(items?.value?.[0]?.PhoneInfo?.phoneNumber ?? '')}
                            {items?.key === 'email' && items?.value?.[0]?.EmailInfo?.emailAddress}
                          </p>
                          {showActiveBrandSection && (
                            <div className="flex items-center">
                              {items?.value?.length > 1 ? <span>& +{items?.value?.length - 1}</span> : <></>}
                              <Button
                                className="p-0 text-blueLight underline"
                                onClick={() => {
                                  setOpenModal(true);
                                  setCurrentEditType(
                                    items?.field === CUSTOMER_DETAILS_HEADER_KEYS.EMAIL_ADDRESS
                                      ? EDIT_EMAIL
                                      : EDIT_PHONE_NUMBER,
                                  );
                                }}
                              >
                                {Texts.manageButton}
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div>
              {accountAddress?.map((items) => (
                <div className="flex gap-2" key={`key-${items?.field}`}>
                  <p className="mb-3 flex-1 font-normal">{items?.field}</p>
                  <p
                    className={`flex-1 font-semibold ${items?.key === 'mailReturnUndeliverable' ? 'hidden' : 'block'} ${
                      items?.mask ? 'gbmask' : ''
                    }`}
                  >
                    {items?.key === 'state' ? state : items?.value}
                  </p>
                  {items?.key === 'mailReturnUndeliverable' && (
                    <p className={`flex-1 font-semibold ${items?.mask ? 'gbmask' : ''}`}>
                      {items?.value ? 'Yes' : 'No'}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default CustomerDetails;
